import { type MouseEvent } from 'react';

import {
  CONTENT_TYPES,
  type ContentListItemType,
  type ItemTypeMap,
} from '@hivebrite/design-system/ContentList';

import { makeConfigUrls as makeForumUrls } from 'hb-react/frontoffice/apps/Forum/utils/configUrls';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import {
  ENTITY_TYPES,
  LEGACY_ENTITY_TYPES,
} from 'hb-react/shared/utils/entitiesContentData/constants';
import {
  type AdminEvent,
  type BusinessOpportunity,
  type Company,
  type EntityItem,
  type File,
  type Folder,
  type ForumDiscussion,
  type News,
  type Post,
  type Project,
  type UserEvent,
  type EntityType,
} from 'hb-react/shared/utils/entitiesContentData/types';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

import { HtmlContent } from './HtmlContent';

type ContentTypeNamesMap = {
  [key in EntityType]?: string;
};

interface TransformerOptions {
  contentTypeNames?: ContentTypeNamesMap;
  displayFields?: {
    [K in CONTENT_TYPES]?: ItemTypeMap[K]['displayFields'];
  };
  flags?: Record<string, boolean>;
  isPublicOffice?: boolean;
  isWhiteLabelGroup?: boolean;
  timeZoneConfiguration?: TimeZoneConfiguration;
  withLink?: boolean;
}

// Helper to transform common event data
const transformCommonEventData = (
  item: AdminEvent | UserEvent,
  options: TransformerOptions,
) => {
  const {
    displayFields,
    isPublicOffice,
    isWhiteLabelGroup,
    timeZoneConfiguration = {},
    withLink,
  } = options;
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timeZoneConfiguration;
  const timezone = eventDatesInUserTimezone
    ? currentUserTimezone
    : item.timezone;

  const date = getDateStr({
    startDate: item.startDate,
    endDate: undefined,
    timezone,
  });

  return {
    contentType: CONTENT_TYPES.EVENT,
    date,
    displayFields: displayFields?.[CONTENT_TYPES.EVENT],
    id: item.id,
    location: item?.location?.location,
    formattedDate: {
      day: dayjs(item.startDate).tz(timezone).format('DD'),
      month: dayjs(item.startDate).tz(timezone).format('MMM'),
    },
    name: item.title,
    attendees: [],
    totalAttendees: 0,
    path: withLink
      ? getEventDedicatedPath({
          hasUser: !isPublicOffice,
          event: {
            type: (
              [
                LEGACY_ENTITY_TYPES.adminEvent,
                ENTITY_TYPES.adminEvent,
              ] as Array<EntityType>
            ).includes(item.type)
              ? 'network_events_event'
              : item.type,
            id: item.id,
          },
          topicId: item.groupCreator?.id,
          isWhitelabelGroup: isWhiteLabelGroup,
        })
      : undefined,
  };
};

const transformUserEvent = (
  item: UserEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  image: item.logo?.mediumUrl,
  ...transformCommonEventData(item, options),
});

const transformAdminEvent = (
  item: AdminEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  onlineEvent: Boolean(item.webinarUrl || item.isInternalZoomMeeting),
  image: item.coverPicture?.mediumUrl,
  ...transformCommonEventData(item, options),
});

// Individual transformers
const transformNews = (
  item: News,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.NEWS] => {
  const { displayFields, flags = {}, isWhiteLabelGroup, withLink } = options;
  const { flagDsIsolationNewsDetailPage } = flags;
  let navigationalProps: { onClick?: () => void; path?: string } = {};

  if (withLink) {
    const path =
      !isWhiteLabelGroup && item.groupCreator
        ? Routes.topic_new_path(item.groupCreator.id, item.id)
        : Routes.new_path(item.id);

    if (flagDsIsolationNewsDetailPage) {
      navigationalProps = {
        path,
        // @ts-expect-error onClick type will be updated within the next package release
        onClick: (event: MouseEvent) => {
          event.preventDefault();
          window.location.href = path;
        },
      };
    } else {
      navigationalProps = { path };
    }
  }

  return {
    contentType: CONTENT_TYPES.NEWS,
    content: item.sanitizedContent,
    displayFields: displayFields?.[CONTENT_TYPES.NEWS],
    id: item.id,
    image: item.coverPicture?.mediumUrl,
    title: item.subject,
    ...navigationalProps,
  };
};

const transformForumDiscussion = (
  item: ForumDiscussion,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FORUM] => {
  const { displayFields, withLink } = options;
  const path = withLink
    ? makeForumUrls(item?.groupCreator?.id).discussionUrl(item.id)
    : undefined;

  return {
    author: {
      id: item.user.id,
      name: item.user.name,
      photoThumbUrl: item.user.photo.thumbnailUrl,
    },
    content: item.sanitizedContent,
    contentType: CONTENT_TYPES.FORUM,
    displayFields: displayFields?.[CONTENT_TYPES.FORUM],
    id: item.id,
    imageSrc: item.coverPicture?.mediumUrl,
    name: item.title,
    path,
  };
};

const transformProject = (
  item: Project,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.PROJECT] => {
  const { displayFields, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator?.id
      ? Routes.topic_venture_path(item.groupCreator.id, item.id)
      : Routes.venture_path(item.id);
  }

  return {
    contentType: CONTENT_TYPES.PROJECT,
    description: item.highLevelPitch,
    displayFields: displayFields?.[CONTENT_TYPES.PROJECT],
    image: item.logo?.mediumUrl,
    location: item.location?.location,
    name: item.companyName,
    path,
  };
};

const transformFolder = (
  item: Folder,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FOLDER] => {
  const { displayFields, withLink } = options;

  let path: string | undefined;

  if (withLink) {
    if (item.type === ENTITY_TYPES.mediaCenterFolder) {
      path = item.groupCreator
        ? Routes.topic_media_center_folder_view_path(
            item.groupCreator.id,
            item.uuid,
          )
        : Routes.media_center_folder_view_path(item.uuid);
    }
    if (item.type === ENTITY_TYPES.portfolioFolder) {
      path = item.user
        ? Routes.user_portfolio_folder_view_path(item.user.id, item.uuid)
        : Routes.portfolios_path();
    }
  }

  let contentTypeName = options.contentTypeNames?.[item.type];
  contentTypeName ||=
    item.type === ENTITY_TYPES.portfolioFolder
      ? __('Portfolio')
      : __('Media Center');

  return {
    contentTypeName,
    contentType: CONTENT_TYPES.FOLDER,
    displayFields: displayFields?.[CONTENT_TYPES.FOLDER],
    folderLabel: __('Folder'),
    name: item.name,
    id: item.id,
    image: item.coverPicture?.mediumUrl ?? '',
    path,
  };
};

const transformFile = (
  item: File,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FILE] => {
  const { displayFields, withLink } = options;

  let path: string | undefined = undefined;

  if (withLink) {
    if (item.type === ENTITY_TYPES.portfolioFile) {
      path = item.user
        ? Routes.user_portfolio_file_view_path(item.user.id, item.uuid)
        : Routes.portfolios_file_view_path(item.uuid);
    } else {
      path = item.groupCreator
        ? Routes.topic_media_center_file_view_path(
            item.groupCreator.id,
            item.uuid,
          )
        : Routes.media_center_file_view_path(item.uuid);
    }
  }

  let contentTypeName = options.contentTypeNames?.[item.type];
  contentTypeName ||=
    item.type === ENTITY_TYPES.portfolioFile
      ? __('Portfolio')
      : __('Media Center');

  return {
    contentTypeName: contentTypeName,
    contentType: CONTENT_TYPES.FILE,
    description: item.description,
    displayFields: displayFields?.[CONTENT_TYPES.FILE],
    extension: item.extension || '',
    fileType: item.fileType || 'application',
    id: item.id,
    image: item.thumbnail?.mediumUrl || item.attachment?.mediumUrl,
    name: item.name,
    path,
    user: item.user
      ? {
          id: item.user.id,
          name: item.user.name,
          path: Routes.user_path(item.user.id),
          photo_thumb_url: item.user.photo.thumbnailUrl,
        }
      : undefined,
  };
};

const transformCompany = (
  item: Company,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.ORGANIZATION] => {
  const { displayFields, withLink } = options;

  const path = withLink ? Routes.company_path(item.id) : undefined;

  return {
    contentType: CONTENT_TYPES.ORGANIZATION,
    description: item.shortDescription,
    displayFields: displayFields?.[CONTENT_TYPES.ORGANIZATION],
    id: item.id,
    image: item.logo?.mediumUrl,
    name: item.name,
    path,
  };
};

const transformBusinessOpportunity = (
  item: BusinessOpportunity,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.OPPORTUNITY] => {
  const { displayFields, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator
      ? Routes.topic_jobs_show_path(item.groupCreator.id, item.id)
      : Routes.opportunity_path(item.id);
  }

  return {
    contentType: CONTENT_TYPES.OPPORTUNITY,
    companyName: item.companyName,
    displayFields: displayFields?.[CONTENT_TYPES.OPPORTUNITY],
    id: item.id,
    image: item.logo?.mediumUrl,
    location: item.location?.location,
    name: item.title || item.companyName,
    path,
  };
};

const transformPost = (
  item: Post,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.POST] => {
  const MAX_CONTENT_LENGTH = 500;
  const { displayFields, withLink } = options;

  let path: string | undefined;
  if (withLink) {
    path = item.groupCreator
      ? Routes.topic_topic_feed_posts_path(item.groupCreator.id, item.id)
      : Routes.feed_post_path(item.id);
  }

  return {
    author: {
      id: item.user.id,
      name: item.user.name,
      photoThumbUrl: item.user.photo.thumbnailUrl,
    },
    content: HtmlContent({
      content: item.contentV2,
      max: MAX_CONTENT_LENGTH,
    }) as unknown as string,
    contentType: CONTENT_TYPES.POST,
    displayFields: displayFields?.[CONTENT_TYPES.POST],
    id: item.id,
    path,
  };
};

const defaultOptions: TransformerOptions = {
  isPublicOffice: true,
  isWhiteLabelGroup: false,
  flags: {},
  withLink: true,
};

export const transformItem = (
  item: EntityItem,
  options: TransformerOptions,
): ContentListItemType | undefined => {
  const opts = { ...defaultOptions, ...options };

  switch (item.type) {
    case ENTITY_TYPES.news:
      return transformNews(item, opts);
    case ENTITY_TYPES.forumDiscussion:
      return transformForumDiscussion(item, opts);
    case ENTITY_TYPES.userEvent:
      return transformUserEvent(item, opts);
    case ENTITY_TYPES.adminEvent:
    case LEGACY_ENTITY_TYPES.adminEvent:
      return transformAdminEvent(item, opts);
    case ENTITY_TYPES.project:
      return transformProject(item, opts);
    case ENTITY_TYPES.company:
      return transformCompany(item, opts);
    case ENTITY_TYPES.mediaCenterFolder:
    case ENTITY_TYPES.portfolioFolder:
      return transformFolder(item, opts);
    case ENTITY_TYPES.mediaCenterFile:
    case ENTITY_TYPES.portfolioFile:
      return transformFile(item, opts);
    case ENTITY_TYPES.opportunity:
    case LEGACY_ENTITY_TYPES.opportunity:
      return transformBusinessOpportunity(item, opts);
    case ENTITY_TYPES.post:
    case LEGACY_ENTITY_TYPES.post:
      return transformPost(item, opts);
    default:
      // @ts-expect-error typescript consider `item` as `never` since all known types are handled above
      // eslint-disable-next-line no-console
      console.error(`Transformer not found for item type: ${item.type}`);
  }

  return undefined;
};

export const transformDataList = (
  items: Array<EntityItem>,
  options: TransformerOptions = {},
): Array<ContentListItemType> =>
  items.map((item) => transformItem(item, options)).filter(Boolean);
